import type { InfraStrukturDB } from 'boot/queryStoreAndCommandBus';

import { AxiosStatic } from 'axios';

import { App } from '@capacitor/app';
import { globalState, simpleObservableStore } from 'boot/simpleObservableStore';
import { IDBPDatabase } from 'idb';
import * as rax from 'retry-axios';
import { v4 as uuidV4 } from 'uuid';

import { CommandProcessIdHandling } from 'src/models/command';
import { AbstractQuery } from 'src/models/query';

import { EventBus } from 'src/util/eventBus/EventBus';
import { NotificationEvent } from 'src/util/events';
import { Event } from 'src/util/events/interfaces/Event';
import { foregroundRefreshGate } from 'src/util/foreground/foregroundRefreshGate';
import { MessageBus } from 'src/util/messagebus/MessageBus';
import { QueryBuilder } from 'src/util/query/QueryBuilder';
import { QueryStoreOfflineDatabase } from 'src/util/query/QueryStoreOfflineDatabase';
import { QueryStoreRequest } from 'src/util/query/models/QueryStoreRequest';
import { ResultRunTypeBaseRecord, ResultRunTypeBaseRecordRunType } from 'src/util/query/models/ResultRecordBaseType';

type LegacyTreeResultRecord = {
	children: Array<unknown> | Record<string, unknown>;
	csrfToken: string;
	keys: Array<string>;
	processIds: Array<string>;
};

const SUBSCRIBED_QUERY_RELOAD_DEBOUNCE_IN_MILLISECONDS = 100;

class QueryStore {
	private appStateChangeListener: Promise<{ remove: () => Promise<void> }> | null = null;
	private abortControllers: Map<string, AbortController> = new Map<string, AbortController>();
	private readonly appVersion: string;
	private axios: AxiosStatic;

	private readonly database: IDBPDatabase<InfraStrukturDB> | null;
	private eventBus: EventBus;
	private readonly instanceName: string;
	private messageBus: MessageBus;
	private queryBuilder: QueryBuilder;
	private queryStoreOfflineDatabase: QueryStoreOfflineDatabase;
	private reloadAllSubscribedQueriesTimeout: ReturnType<typeof setTimeout> | null = null;
	private readonly reloadTheQueryIfNecessaryListener = this.reloadTheQueryIfNecessary.bind(this);
	private readonly reloadTheQueryIfNecessaryOnAppStateChange = ({ isActive }: { isActive: boolean }): void => {
		if (!isActive) {
			foregroundRefreshGate.markBackgrounded();
			return;
		}

		if (foregroundRefreshGate.shouldRefreshAfterForeground()) {
			this.scheduleReloadOfSubscribedQueries();
		}
	};
	private readonly reloadTheQueryIfNecessaryOnLogout = (): void => {
		this.clearSubscriptions();
	};
	private readonly reloadTheQueryIfNecessaryOnOnline = (): void => {
		this.scheduleReloadOfSubscribedQueries();
	};
	private readonly reloadingSubscribedQueryRequests: Set<string> = new Set<string>();
	private readonly rerunRequestedSubscribedQueryRequests: Map<string, QueryStoreRequest<never>> = new Map<
		string,
		QueryStoreRequest<never>
	>();
	private retryInterceptorAttached = false;
	private readonly requestRunGenerationByQueryIdentifier: Map<string, number> = new Map<string, number>();
	private readonly subscriptionGenerationByQueryIdentifier: Map<string, number> = new Map<string, number>();
	private readonly subscriptionGenerationByRequest: WeakMap<QueryStoreRequest<never>, number> = new WeakMap<
		QueryStoreRequest<never>,
		number
	>();
	private subscribedKeys: Map<string, Array<QueryStoreRequest<never>>> = new Map<
		string,
		Array<QueryStoreRequest<never>>
	>();
	private readonly user: string;
	private readonly withCredentials: boolean | undefined;

	constructor(
		queryBuilder: QueryBuilder,
		messageBus: MessageBus,
		eventBus: EventBus,
		axios: AxiosStatic,
		database: IDBPDatabase<InfraStrukturDB> | null,
		instanceName: string,
		user: string,
		appVersion: string,
		withCredentials?: boolean
	) {
		this.queryBuilder = queryBuilder;
		this.messageBus = messageBus;
		this.axios = axios;
		this.database = database;
		this.instanceName = instanceName;
		this.user = user;
		this.appVersion = appVersion;
		this.withCredentials = withCredentials;
		this.queryStoreOfflineDatabase = new QueryStoreOfflineDatabase(
			this.database,
			this.instanceName,
			this.user,
			this.appVersion
		);
		this.eventBus = eventBus;
		this.initializeTheAutomaticRefresh();
		this.eventBus.on('logout', this.reloadTheQueryIfNecessaryOnLogout);
	}

	private initializeTheAutomaticRefresh(): void {
		this.messageBus.subscribe(new NotificationEvent({}), this.reloadTheQueryIfNecessaryListener);

		if (App !== undefined && typeof App.addListener === 'function') {
			this.appStateChangeListener = Promise.resolve(
				App.addListener('appStateChange', this.reloadTheQueryIfNecessaryOnAppStateChange)
			);
		}

		if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
			window.addEventListener('online', this.reloadTheQueryIfNecessaryOnOnline);
		}
	}

	private queryRequestIdentifier<DesiredResponseType>(
		queryStoreRequest: QueryStoreRequest<DesiredResponseType>
	): string {
		return `${queryStoreRequest.query.uniqueQueryIdentifier()}-${queryStoreRequest.callback.uniqueCallbackIdentifier()}`;
	}

	private reloadSubscribedQuery<DesiredResponseType>(
		queryStoreRequest: QueryStoreRequest<DesiredResponseType>
	): void {
		const queryRequestIdentifier = this.queryRequestIdentifier(queryStoreRequest);
		if (this.reloadingSubscribedQueryRequests.has(queryRequestIdentifier)) {
			this.rerunRequestedSubscribedQueryRequests.set(
				queryRequestIdentifier,
				queryStoreRequest as unknown as QueryStoreRequest<never>
			);

			return;
		}

		this.reloadingSubscribedQueryRequests.add(queryRequestIdentifier);
		void this.query(queryStoreRequest, true).finally(() => {
			this.reloadingSubscribedQueryRequests.delete(queryRequestIdentifier);
			const rerunRequest = this.rerunRequestedSubscribedQueryRequests.get(queryRequestIdentifier);
			if (rerunRequest !== undefined) {
				this.rerunRequestedSubscribedQueryRequests.delete(queryRequestIdentifier);
				this.reloadSubscribedQuery(rerunRequest);
			}
		});
	}

	private reloadTheQueryIfNecessary(event: Event): void {
		event = <NotificationEvent>event;
		const eventPayload: Record<string, Array<string>> = event.eventPayload as Record<string, Array<string>>;
		const keys: Array<string> = eventPayload['keys'] ?? [];
		const processIds: Array<string> = eventPayload['processIds'] ?? [];
		const queryRequestsLoadedByTheEvent: Map<string, QueryStoreRequest<never>> = new Map<
			string,
			QueryStoreRequest<never>
		>();

		void this.queryStoreOfflineDatabase.removeByQueryKeys(keys);
		keys.forEach((key: string) => {
			if (this.subscribedKeys.has(key)) {
				const queryStoreRequests: Array<QueryStoreRequest<never>> | undefined = this.subscribedKeys.get(key);

				if (queryStoreRequests != undefined) {
					queryStoreRequests.forEach((queryStoreRequest) => {
						const queryRequestIdentifier = this.queryRequestIdentifier(queryStoreRequest);
						const previousQueryRequest = queryRequestsLoadedByTheEvent.get(queryRequestIdentifier);

						if (
							(queryStoreRequest.query.processId == CommandProcessIdHandling.IGNORING ||
								(queryStoreRequest.query.processId != CommandProcessIdHandling.IGNORING &&
									!processIds.includes(queryStoreRequest.query.processId))) &&
							(previousQueryRequest === undefined ||
								previousQueryRequest.createTime < queryStoreRequest.createTime)
						) {
							queryRequestsLoadedByTheEvent.set(queryRequestIdentifier, queryStoreRequest);
						}
					});
				}
			}
		});

		queryRequestsLoadedByTheEvent.forEach((queryStoreRequest) => {
			this.reloadSubscribedQuery(queryStoreRequest);
		});
	}

	private reloadAllQueriesAfterAppStateChange(): void {
		this.getUniqueSubscribedQueryRequests().forEach((queryStoreRequest) => {
			this.reloadSubscribedQuery(queryStoreRequest);
		});
	}

	private scheduleReloadOfSubscribedQueries(): void {
		if (this.reloadAllSubscribedQueriesTimeout !== null) {
			return;
		}

		this.reloadAllSubscribedQueriesTimeout = setTimeout(() => {
			this.reloadAllSubscribedQueriesTimeout = null;
			this.reloadAllQueriesAfterAppStateChange();
		}, SUBSCRIBED_QUERY_RELOAD_DEBOUNCE_IN_MILLISECONDS);
	}

	private tokenIdentifier<DesiredResponseType>(queryStoreRequest: QueryStoreRequest<DesiredResponseType>): string {
		return queryStoreRequest.query.uniqueQueryIdentifier();
	}

	private supersedeSubscriptionTrackingForQueryIdentifier(queryIdentifier: string): void {
		const currentGeneration = this.subscriptionGenerationByQueryIdentifier.get(queryIdentifier) ?? 0;
		this.subscriptionGenerationByQueryIdentifier.set(queryIdentifier, currentGeneration + 1);
	}

	private registerRequestRunForQueryIdentifier(queryIdentifier: string): number {
		const nextGeneration = (this.requestRunGenerationByQueryIdentifier.get(queryIdentifier) ?? 0) + 1;
		this.requestRunGenerationByQueryIdentifier.set(queryIdentifier, nextGeneration);
		return nextGeneration;
	}

	private isSupersededByNewerRequestRun(queryIdentifier: string, requestRunGeneration: number): boolean {
		return (this.requestRunGenerationByQueryIdentifier.get(queryIdentifier) ?? 0) !== requestRunGeneration;
	}

	private registerSubscriptionGenerationForQueryRequest<DesiredResponseType>(
		queryStoreRequest: QueryStoreRequest<DesiredResponseType>
	): void {
		if (!queryStoreRequest.query.shouldSubscribeForUpdates()) {
			return;
		}

		const queryIdentifier = queryStoreRequest.query.uniqueQueryIdentifier();
		const nextGeneration = (this.subscriptionGenerationByQueryIdentifier.get(queryIdentifier) ?? 0) + 1;

		this.subscriptionGenerationByQueryIdentifier.set(queryIdentifier, nextGeneration);
		this.subscriptionGenerationByRequest.set(queryStoreRequest as QueryStoreRequest<never>, nextGeneration);
	}

	private isCurrentSubscriptionRequest<DesiredResponseType>(
		queryStoreRequest: QueryStoreRequest<DesiredResponseType>
	): boolean {
		if (!queryStoreRequest.query.shouldSubscribeForUpdates()) {
			return true;
		}

		const expectedGeneration = this.subscriptionGenerationByRequest.get(
			queryStoreRequest as QueryStoreRequest<never>
		);

		if (expectedGeneration === undefined) {
			return false;
		}

		return (
			this.subscriptionGenerationByQueryIdentifier.get(queryStoreRequest.query.uniqueQueryIdentifier()) ===
			expectedGeneration
		);
	}

	private abortRunningRequestForQueryIdentifier(queryIdentifier: string): void {
		const storedAbortController = this.abortControllers.get(queryIdentifier);

		if (storedAbortController === undefined) {
			return;
		}

		storedAbortController.abort();
		this.abortControllers.delete(queryIdentifier);
	}

	checkResultRunTypeBaseRecord(value: unknown): value is ResultRunTypeBaseRecord {
		if (value === null) {
			return false;
		}

		if (typeof value !== 'object') {
			return false;
		}

		if (!('keys' in value)) {
			return false;
		}

		if (!('processIds' in value)) {
			return false;
		}

		if (!('totalCount' in value)) {
			return false;
		}

		if (!('csrfToken' in value)) {
			return false;
		}

		return 'results' in value;
	}

	checkLegacyTreeResultRecord(value: unknown): value is LegacyTreeResultRecord {
		if (value === null || typeof value !== 'object') {
			return false;
		}

		if (!('keys' in value) || !('processIds' in value) || !('csrfToken' in value)) {
			return false;
		}

		if (!('children' in value)) {
			return false;
		}

		const legacyTreeValue = value as {
			children?: unknown;
			csrfToken?: unknown;
			keys?: unknown;
			processIds?: unknown;
		};

		return (
			typeof legacyTreeValue.csrfToken === 'string' &&
			Array.isArray(legacyTreeValue.keys) &&
			Array.isArray(legacyTreeValue.processIds)
		);
	}

	clearSubscriptions(): void {
		this.abortControllers.forEach((_controller, queryIdentifier) => {
			this.supersedeSubscriptionTrackingForQueryIdentifier(queryIdentifier);
		});
		this.abortControllers.forEach((_controller, queryIdentifier) => {
			this.abortRunningRequestForQueryIdentifier(queryIdentifier);
		});
		this.getUniqueSubscribedQueryRequests().forEach((queryStoreRequest) => {
			this.supersedeSubscriptionTrackingForQueryIdentifier(queryStoreRequest.query.uniqueQueryIdentifier());
		});
		this.subscribedKeys.clear();
	}

	getSubscribedKeys() {
		return this.subscribedKeys;
	}

	private isAbortedRequestError(error: unknown): error is { name: string } {
		if (typeof error !== 'object' || error === null || !('name' in error)) {
			return false;
		}

		return error.name === 'AbortError' || error.name === 'CanceledError';
	}

	private attachRetryInterceptor(): void {
		if (this.retryInterceptorAttached) {
			return;
		}

		try {
			void rax.attach(this.axios);
			this.retryInterceptorAttached = true;
		} catch {
			// Custom axios shims in tests may not support retry-axios.
		}
	}

	private sanitizeLegacyTreeResult(result: unknown): unknown {
		if (Array.isArray(result)) {
			return result.filter((item) => item !== null).map((item) => this.sanitizeLegacyTreeResult(item));
		}

		if (result === null || typeof result !== 'object') {
			return result;
		}

		return Object.fromEntries(
			Object.entries(result as Record<string, unknown>).map(([key, value]) => [
				key,
				this.sanitizeLegacyTreeResult(value),
			])
		);
	}

	private validateQueryResult(queryStoreRequest: QueryStoreRequest<unknown>, result: unknown): void {
		if (result === null || result === undefined) {
			return;
		}

		if (Array.isArray(result)) {
			result.forEach((item) => {
				queryStoreRequest.runType.check(item);
			});
			return;
		}

		queryStoreRequest.runType.check(result);
	}

	private getUniqueSubscribedQueryRequests(): Array<QueryStoreRequest<never>> {
		const uniqueQueryRequests = new Map<string, QueryStoreRequest<never>>();

		this.subscribedKeys.forEach((queryStoreRequests: Array<QueryStoreRequest<never>>) => {
			queryStoreRequests.forEach((queryStoreRequest) => {
				const uniqueRequestKey = this.queryRequestIdentifier(queryStoreRequest);
				const existingQueryStoreRequest = uniqueQueryRequests.get(uniqueRequestKey);

				if (
					existingQueryStoreRequest === undefined ||
					existingQueryStoreRequest.createTime < queryStoreRequest.createTime
				) {
					uniqueQueryRequests.set(uniqueRequestKey, queryStoreRequest);
				}
			});
		});

		return Array.from(uniqueQueryRequests.values());
	}

	async query<DesiredResponseType>(
		queryStoreRequest: QueryStoreRequest<DesiredResponseType>,
		ignoreCachedResult = false
	): Promise<void> {
		this.attachRetryInterceptor();
		let tokenIdentifier = '';
		let requestRegisteredForLoadingIndicator = false;
		const requestId = uuidV4();
		let requestWasFinalized = false;
		let networkResponseSettled = false;

		const finalizeQueryRequest = (): void => {
			if (requestWasFinalized) {
				return;
			}

			requestWasFinalized = true;
			this.removeTheAbortController(tokenIdentifier);

			if (requestRegisteredForLoadingIndicator) {
				simpleObservableStore.runningRequest = simpleObservableStore.runningRequest.filter(
					(request) => request.requestId !== requestId
				);
			}

			queryStoreRequest.callback.finalizeCallbackFunction();
		};

		if (queryStoreRequest.responseType === 'blob') {
			await this.queryBlob<DesiredResponseType>(queryStoreRequest);
		} else {
			try {
				const controller = new AbortController();
				tokenIdentifier = this.tokenIdentifier(queryStoreRequest);
				this.registerSubscriptionGenerationForQueryRequest(queryStoreRequest);
				const requestRunGeneration = this.registerRequestRunForQueryIdentifier(tokenIdentifier);

				if (!this.abortControllers.has(tokenIdentifier)) {
					this.abortControllers.set(tokenIdentifier, controller);
				} else {
					const storedAbortController = this.abortControllers.get(tokenIdentifier) as AbortController;
					if (storedAbortController !== undefined) {
						storedAbortController.abort();
						this.removeTheAbortController(tokenIdentifier);
						this.abortControllers.set(tokenIdentifier, controller);
					}
				}

				simpleObservableStore.cleanExpiredRequests();
				simpleObservableStore.runningRequest.push({
					activeSince: new Date(),
					requestId: requestId,
				});
				requestRegisteredForLoadingIndicator = true;

				if (
					queryStoreRequest.query.isCacheable() &&
					!queryStoreRequest.query.shouldIgnoreCachedResult() &&
					!ignoreCachedResult
				) {
					void this.queryStoreOfflineDatabase
						.load<DesiredResponseType>(queryStoreRequest)
						.then((resultFromDB) => {
							if (
								resultFromDB !== undefined &&
								!networkResponseSettled &&
								this.isCurrentSubscriptionRequest(queryStoreRequest) &&
								!this.isSupersededByNewerRequestRun(tokenIdentifier, requestRunGeneration)
							) {
								queryStoreRequest.callback.successCallbackFunction(
									resultFromDB.successResponse,
									resultFromDB.baseRecordSuccessResponse
								);
							}
						});
				}

				const response = await this.axios.get<ResultRunTypeBaseRecord | string>(
					this.queryBuilder.build(queryStoreRequest.query).queryRequestURL,
					{
						headers: {
							accept: 'application/x-compress',
						},
						raxConfig: {
							backoffType: 'exponential',

							httpMethodsToRetry: ['GET', 'HEAD', 'OPTIONS', 'DELETE', 'PUT'],

							retry: 5,

							retryDelay: 1000,

							statusCodesToRetry: [
								[100, 199],
								[429, 429],
								[500, 599],
							],
						},
						responseType: queryStoreRequest.responseType,
						signal: controller.signal,
						...(this.withCredentials !== undefined ? { withCredentials: this.withCredentials } : {}),
					}
				);

				networkResponseSettled = true;

				if (response.data === '') {
					// Silent server no-op: no success/failure callback runs, so a list cleared
					// before this fetch stays empty without an error state. Log it so support
					// can distinguish this from a legitimate empty result. (Semantics on purpose
					// unchanged until the backend contract for empty envelopes is clarified.)
					console.warn(
						'[QueryStore] Empty response envelope for',
						queryStoreRequest.query.uniqueQueryIdentifier()
					);
					finalizeQueryRequest();
					return;
				} else if (this.checkResultRunTypeBaseRecord(response.data)) {
					if (this.isSupersededByNewerRequestRun(tokenIdentifier, requestRunGeneration)) {
						finalizeQueryRequest();
						return;
					}

					ResultRunTypeBaseRecordRunType.check(response.data);
					globalState.csrfToken = response.data.csrfToken;
					this.validateQueryResult(queryStoreRequest as QueryStoreRequest<unknown>, response.data.results);

					queryStoreRequest.callback.successCallbackFunction(
						response.data.results as DesiredResponseType,
						response.data
					);

					if (queryStoreRequest.query.isCacheable()) {
						await this.queryStoreOfflineDatabase.removeByDatabaseKey(
							this.queryStoreOfflineDatabase.databaseKeyFor(queryStoreRequest)
						);

						void this.queryStoreOfflineDatabase.save<DesiredResponseType>(queryStoreRequest, {
							baseRecordSuccessResponse: response.data,
							successResponse: response.data.results as DesiredResponseType,
						});
					}

					if (
						queryStoreRequest.query.shouldSubscribeForUpdates() &&
						this.isCurrentSubscriptionRequest(queryStoreRequest)
					) {
						response.data.keys.forEach((key: string) => {
							let queryStoreRequestsForTheKey: Array<QueryStoreRequest<never>> | undefined =
								this.subscribedKeys.get(key);

							if (queryStoreRequestsForTheKey === undefined) {
								queryStoreRequestsForTheKey = [];
							}

							queryStoreRequestsForTheKey.push(queryStoreRequest);

							const result = this.removeDuplicates(
								queryStoreRequestsForTheKey,
								(a, b) =>
									`${a.query.uniqueQueryIdentifier()}-${a.callback.uniqueCallbackIdentifier()}` ==
										`${b.query.uniqueQueryIdentifier()}-${b.callback.uniqueCallbackIdentifier()}` &&
									a.createTime >= b.createTime
							);

							if (result.length > 0) {
								this.subscribedKeys.set(key, result);
							} else {
								this.subscribedKeys.delete(key);
							}
						});
					}

					finalizeQueryRequest();
					return;
				} else if (this.checkLegacyTreeResultRecord(response.data)) {
					if (this.isSupersededByNewerRequestRun(tokenIdentifier, requestRunGeneration)) {
						finalizeQueryRequest();
						return;
					}

					const queryResult = this.sanitizeLegacyTreeResult(response.data.children);
					globalState.csrfToken = response.data.csrfToken;
					this.validateQueryResult(queryStoreRequest as QueryStoreRequest<unknown>, queryResult);

					queryStoreRequest.callback.successCallbackFunction(queryResult as DesiredResponseType);

					if (
						queryStoreRequest.query.shouldSubscribeForUpdates() &&
						this.isCurrentSubscriptionRequest(queryStoreRequest)
					) {
						response.data.keys.forEach((key: string) => {
							let queryStoreRequestsForTheKey: Array<QueryStoreRequest<never>> | undefined =
								this.subscribedKeys.get(key);

							if (queryStoreRequestsForTheKey === undefined) {
								queryStoreRequestsForTheKey = [];
							}

							queryStoreRequestsForTheKey.push(queryStoreRequest);

							const result = this.removeDuplicates(
								queryStoreRequestsForTheKey,
								(a, b) =>
									`${a.query.uniqueQueryIdentifier()}-${a.callback.uniqueCallbackIdentifier()}` ==
										`${b.query.uniqueQueryIdentifier()}-${b.callback.uniqueCallbackIdentifier()}` &&
									a.createTime >= b.createTime
							);

							if (result.length > 0) {
								this.subscribedKeys.set(key, result);
							} else {
								this.subscribedKeys.delete(key);
							}
						});
					}

					finalizeQueryRequest();
					return;
				}

				finalizeQueryRequest();
				queryStoreRequest.callback.failureCallbackFunction(new Error('Unexpected query response envelope.'));
			} catch (error: Error | unknown) {
				networkResponseSettled = true;
				finalizeQueryRequest();

				if (this.isAbortedRequestError(error)) {
					return;
				}

				queryStoreRequest.callback.failureCallbackFunction(error);
			}
		}
	}

	async queryBlob<DesiredResponseType>(queryStoreRequest: QueryStoreRequest<DesiredResponseType>): Promise<void> {
		let tokenIdentifier = '';
		let controller: AbortController | undefined;

		try {
			controller = new AbortController();
			tokenIdentifier = this.tokenIdentifier(queryStoreRequest);

			if (!this.abortControllers.has(tokenIdentifier)) {
				this.abortControllers.set(tokenIdentifier, controller);
			} else {
				const storedAbortController = this.abortControllers.get(tokenIdentifier) as AbortController;
				if (storedAbortController !== undefined) {
					storedAbortController.abort();
					this.removeTheAbortController(tokenIdentifier);
					this.abortControllers.set(tokenIdentifier, controller);
				}
			}

			const response = await this.axios.get<DesiredResponseType>(
				this.queryBuilder.build(queryStoreRequest.query).queryRequestURL,
				{
					responseType: queryStoreRequest.responseType,
					signal: controller.signal,
					...(this.withCredentials !== undefined ? { withCredentials: this.withCredentials } : {}),
				}
			);

			queryStoreRequest.runType.check(response);

			queryStoreRequest.callback.successCallbackFunction(response.data);

			return;
		} catch (error: Error | unknown) {
			if (this.isAbortedRequestError(error)) {
				return;
			}
			queryStoreRequest.callback.failureCallbackFunction(error);
		} finally {
			if (controller !== undefined && this.abortControllers.get(tokenIdentifier) === controller) {
				this.removeTheAbortController(tokenIdentifier);
			}
			queryStoreRequest.callback.finalizeCallbackFunction();
		}
	}

	removeDuplicates<T>(array: T[], compareFn: (a: T, b: T) => boolean): T[] {
		const uniqueArray: T[] = [];

		for (const item of array) {
			const index = uniqueArray.findIndex((existingItem) => compareFn(item, existingItem));
			if (index !== -1) {
				uniqueArray[index] = item;
			} else {
				uniqueArray.push(item);
			}
		}

		return uniqueArray;
	}

	removeTheAbortController(tokenIdentifier: string): void {
		if (tokenIdentifier !== '' && this.abortControllers.has(tokenIdentifier)) {
			const storedAbortController = this.abortControllers.get(tokenIdentifier) as AbortController;
			if (storedAbortController !== undefined) {
				this.abortControllers.delete(tokenIdentifier);
			}
		}
	}

	unsubscribeQuery(query: AbstractQuery): Promise<void> {
		const queryIdentifier = query.uniqueQueryIdentifier();
		this.supersedeSubscriptionTrackingForQueryIdentifier(queryIdentifier);
		this.abortRunningRequestForQueryIdentifier(queryIdentifier);

		this.subscribedKeys.forEach((item, key, map) => {
			const filteredQueryStoreRequests = item.filter((queryFromRequest) => {
				return queryFromRequest.query.uniqueQueryIdentifier() !== queryIdentifier;
			});
			if (filteredQueryStoreRequests.length > 0) {
				map.set(key, filteredQueryStoreRequests);
			} else {
				map.delete(key);
			}
		});

		return Promise.resolve();
	}

	dispose(): void {
		if (this.reloadAllSubscribedQueriesTimeout !== null) {
			clearTimeout(this.reloadAllSubscribedQueriesTimeout);
			this.reloadAllSubscribedQueriesTimeout = null;
		}

		this.messageBus.unsubscribe(new NotificationEvent({}), this.reloadTheQueryIfNecessaryListener);
		this.eventBus.off('logout', this.reloadTheQueryIfNecessaryOnLogout);
		window.removeEventListener('online', this.reloadTheQueryIfNecessaryOnOnline);

		if (this.appStateChangeListener !== null) {
			void this.appStateChangeListener.then((listener) => listener.remove());
			this.appStateChangeListener = null;
		}

		this.clearSubscriptions();
		this.reloadingSubscribedQueryRequests.clear();
		this.rerunRequestedSubscribedQueryRequests.clear();
		this.subscriptionGenerationByQueryIdentifier.clear();
	}
}

export { QueryStore };
